// composables/makeImage.ts
import { reactive, computed, shallowRef } from 'vue';
import { useRequestTrackerStore } from '@/libraries/vue-fetch/stores/requestTracker';
import { decode } from 'blurhash';

export function makeImage(width?: number, height?: number): ReactiveImage {
  return new ReactiveImage(width, height);
}

export class ReactiveImage extends Image implements HTMLImageElement {
  private reactive = reactive<{ loaded: boolean; currentSrc: string }>({
    loaded: false,
    currentSrc: '',
  });

  public loaded = computed(() => this.reactive.loaded);

  /**
   * Inner ReactiveImage that pre-loads the thumbnail URL independently of
   * this instance's own src/loaded pipeline (reserved for the real image).
   */
  private _thumbnailImage = shallowRef<ReactiveImage | null>(null);

  /**
   * Store the `src` of the thumbnail to  preload.
   */
  private _thumbnailDisplaySrc = shallowRef<string>('');

  /**
   * Reactive src that progresses through three stages:
   * 1. Blurhash data URL  — immediate, decoded in the constructor
   * 2. Thumbnail URL      — once the inner ReactiveImage has fully loaded it
   * 3. Real image URL     — once this instance's own src has loaded
   */
  public displaySrc = computed(() => {
    if (this.reactive.loaded) return this.reactive.currentSrc;
    const thumb = this._thumbnailImage.value;
    if (thumb?.loaded.value) return this._thumbnailDisplaySrc.value || thumb.src;
    return this._blurhashDataUrl ?? '';
  });

  private trackerStore = useRequestTrackerStore();

  private requestId?: string;

  private _requestedWidth?: number;

  private _requestedHeight?: number;

  /** Data URL produced from the blurhash, used as a placeholder. */
  private _blurhashDataUrl: string | null = null;

  /**
   * True while the load event fired is for the blurhash placeholder data URL,
   * not the real image. Prevents marking `loaded` as true prematurely.
   */
  private _isBlurhashPlaceholder = false;

  constructor(
    width?: number,
    height?: number,
    blurhash?: string,
    thumbnailUrl?: string,
  ) {
    super(width, height);
    this._requestedWidth = width;
    this._requestedHeight = height;
    this.addEventListener('load', this.afterLoad);
    this.addEventListener('error', this.afterError);

    if (blurhash) {
      this._setBlurhashSrc(blurhash, width, height);
    }
    if (thumbnailUrl) {
      // Load the thumbnail via a nested ReactiveImage so its loaded state is
      // tracked reactively without touching this instance's own src / loaded
      // pipeline (which is reserved for the real full-resolution image).
      const inner = new ReactiveImage();
      inner.addEventListener('load', () => {
        this._thumbnailDisplaySrc.value = this._buildThumbnailDisplaySrc(inner);
      });
      inner.src = thumbnailUrl;
      this._thumbnailImage.value = inner;
    }
  }

  private _renderSourceToDataUrl(
    source: CanvasImageSource,
    width: number,
    height: number,
  ): string | null {
    const output = document.createElement('canvas');
    output.width = width;
    output.height = height;
    const ctx = output.getContext('2d');
    if (!ctx) return null;
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(source, 0, 0, width, height);
    return output.toDataURL();
  }

  private _buildThumbnailDisplaySrc(image: HTMLImageElement): string {
    const width = this._requestedWidth;
    const height = this._requestedHeight;
    if (!width || !height) {
      return image.src;
    }
    if (image.naturalWidth === width && image.naturalHeight === height) {
      return image.src;
    }
    return this._renderSourceToDataUrl(image, width, height) ?? image.src;
  }

  private _setBlurhashSrc(
    hash: string,
    width?: number,
    height?: number,
  ) {
    try {
      const W = 32;
      const H = 32;
      const pixels = decode(hash, W, H);

      // Decode into a small 32×32 canvas first (fast, size-independent)
      const small = document.createElement('canvas');
      small.width = W;
      small.height = H;
      const ctx = small.getContext('2d');
      if (!ctx) return;
      const imageData = ctx.createImageData(W, H);
      imageData.data.set(pixels);
      ctx.putImageData(imageData, 0, 0);

      this._blurhashDataUrl = width && height
        ? (this._renderSourceToDataUrl(small, width, height) ?? small.toDataURL())
        : small.toDataURL();
      this._isBlurhashPlaceholder = true;
      super.src = this._blurhashDataUrl;
    } catch {
      // ignore invalid blurhash
    }
  }

  updateProgress(percent: number) {
    if (!this.requestId) {
      return;
    }
    this.trackerStore.updateRequest(this.requestId, { progress: percent });
  }

  override set src(value: string) {
    // If the blurhash data URL hasn't fired its load event yet, clear the flag
    // so the upcoming real-image load event is handled correctly.
    this._isBlurhashPlaceholder = false;
    if (this.requestId) {
      this.trackerStore.removeRequest(this.requestId);
    }

    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    this.requestId = id;

    this.trackerStore.addRequest({
      type: 'img',
      id,
      url: new URL(value, location.href),
      method: 'GET',
      state: 'pending',
      progress: 0,
    });

    this.reactive.loaded = false;
    this.reactive.currentSrc = value;
    super.src = value;
    this.updateProgress(10);
  }

  override get src() {
    return super.src;
  }

  private afterLoad = () => {
    if (this._isBlurhashPlaceholder) {
      this._isBlurhashPlaceholder = false;
      return;
    }
    this.reactive.loaded = true;
    if (this.requestId) {
      this.updateProgress(100);
      this.trackerStore.finishedRequest(this.requestId, new Response(null, { status: 200 }));
    }
  };

  private afterError = (e: Event | string) => {
    if (this.requestId) {
      const err = new Error(typeof e === 'string' ? e : 'Image failed to load');
      this.trackerStore.failedRequest(this.requestId, err);
    }
  };
}
